import { Alert, CardContent } from '@mui/material';
import { CustomTabbedFormProvider, useCustomTabbedForm } from 'components/TabbedForm';
import { ShowTabButton } from 'components/TabButtons/ToTabButtons';
import { ComponentType, FC, ReactNode, useEffect } from 'react';
import {
  Edit,
  EditProps,
  FormTabProps,
  TabbedForm,
  TabbedFormProps,
  TopToolbar,
  useEditController,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UnitEditApps, UnitEditConfig, UnitEditDev, UnitEditInfo } from './components';
import { UnitEditMobileNetwork } from './components/UnitEditMobileNetwork';

const UnitActions = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  return (
    <>
      <TopToolbar>
        <ShowTabButton />
      </TopToolbar>

      {record?.developer_options?.staging_api && (
        <Alert variant="filled" severity="warning" sx={{ mb: 2 }}>
          {translate('resources.units.uses_staging_api')}
        </Alert>
      )}
    </>
  );
};
const UnitEdit: FC<EditProps> = (props) => {
  const { id } = useParams();
  const { isLoading } = useEditController({ resource: 'units', id });

  if (isLoading) return null;

  return (
    <Edit mutationMode="pessimistic" {...props} redirect={false} actions={<UnitActions />}>
      <CustomTabbedFormProvider>
        <UnitEditTabs />
      </CustomTabbedFormProvider>
    </Edit>
  );
};

/**
 * The tabs of the edit form, in render order. Single source of truth: the form below renders
 * this list, and the default-tab redirect takes its target from the first entry, so there is no
 * second copy of a tab path to keep in sync.
 */
const UNIT_EDIT_TABS: ReadonlyArray<{ path: string; label: string; Component: ComponentType<FormTabProps> }> = [
  { path: 'info', label: 'resources.units.tabs.info', Component: UnitEditInfo },
  { path: 'configuration', label: 'resources.units.tabs.configuration', Component: UnitEditConfig },
  { path: 'apps', label: 'resources.units.tabs.apps', Component: UnitEditApps },
  { path: 'developer_options', label: 'resources.units.tabs.developer_options', Component: UnitEditDev },
  { path: 'mobile_network', label: 'resources.units.tabs.mobile_network', Component: UnitEditMobileNetwork },
];

const DEFAULT_UNIT_EDIT_TAB_PATH = UNIT_EDIT_TABS[0].path;

/**
 * The tab-less edit URL, with or without a trailing slash: /units/5 and /units/5/ — and nothing
 * else. Capturing everything up to the optional trailing slash is what makes the redirect emit
 * /units/5/info rather than /units/5//info.
 *
 * TVR-2143: the guard this replaced tested for a slash AFTER the id, which the real edit URL
 * does not have, so it never fired there (TabbedFormView then hid every tab and the page showed
 * headers and no fields), while on /units/5/ it appended /info to a path that still failed its
 * own second test and looped without bound. This shape has neither problem: the redirect target
 * carries a segment after the id, so it no longer matches and the effect cannot re-fire.
 *
 * A path that already names a segment after the id is left alone — the declared tab paths
 * (including the underscored developer_options and mobile_network), the show route, an unknown
 * segment such as /units/5/nonsense, and any future sub-route. An unknown segment deliberately
 * does NOT redirect: rewriting a URL this component does not own is how a nested route gets
 * hijacked, and the tab bar still offers every real tab.
 */
const UNIT_EDIT_ROOT_PATH = /^(.*\/units\/\d+)\/?$/;

/**
 * Everything `<TabbedForm>` accepts, minus the four things this component decides for itself and
 * therefore overrides after the spread: `children`, `component`, `onSubmit`, and
 * `warnWhenUnsavedChanges`. Every remaining key is optional because the only call site — `<UnitEditTabs />`
 * inside `<CustomTabbedFormProvider>` above — passes nothing; the spread is there so a future caller
 * can hand a TabbedForm option through without this signature needing a rewrite.
 */
type UnitEditTabsProps = Partial<
  Omit<TabbedFormProps, 'children' | 'component' | 'onSubmit' | 'warnWhenUnsavedChanges'>
>;

const UnitEditTabs = (props: UnitEditTabsProps) => {
  const { handleSubmit } = useCustomTabbedForm();
  const navigate = useNavigate();
  const currentPath = useLocation();

  useEffect(() => {
    const editRoot = UNIT_EDIT_ROOT_PATH.exec(currentPath.pathname);

    if (editRoot) {
      // replace: the default-tab redirect must not leave a history entry, otherwise Back pops to
      // /units/:id, this effect re-fires and the user is trapped on the page.
      navigate(`${editRoot[1]}/${DEFAULT_UNIT_EDIT_TAB_PATH}`, { replace: true });
    }
  }, [currentPath.pathname, navigate]);

  return (
    <TabbedForm {...props} onSubmit={handleSubmit} component={DefaultComponent} warnWhenUnsavedChanges>
      {UNIT_EDIT_TABS.map(({ path, label, Component }) => (
        <Component key={path} label={label} path={path} value={path} />
      ))}
    </TabbedForm>
  );
};

/** Passed to `<TabbedForm component=...>`, which renders it around the tab panels. */
const DefaultComponent = ({ children }: { children?: ReactNode }) => {
  return <CardContent>{children}</CardContent>;
};

export default UnitEdit;
